/**
 * Upgrades a plain textarea into a bold/italic editor.
 *
 * PROGRESSIVE ENHANCEMENT, AND THE TEXTAREA IS THE REAL FIELD. With scripting off the member gets a
 * working plain-text bio and the server encodes every character they typed. This hides the textarea,
 * puts a contenteditable in front of it, and syncs back on every keystroke — so the form still posts
 * one field, and the hidden flag tells the server which shape that field is in rather than leaving
 * it to sniff for tags.
 *
 * Everything produced here is sanitised server-side to six tags and no attributes, so the browser
 * differences below (`<b>` vs `<strong>`, `<div>` vs `<p>` wrappers) do not matter — they are
 * normalised on the way into storage.
 */

/**
 * `execCommand` is formally deprecated and still the only one-line way to toggle formatting across a
 * selection in every browser. The Selection/Range alternative is an order of magnitude more code for
 * these two commands, and would have to reimplement splitting a selection across element boundaries.
 * If it is ever removed, that is the day this becomes a real editor component.
 */
const COMMANDS = ["bold", "italic"];

/**
 * The textarea's `maxlength` used to stop the typing. A field holding HTML cannot carry one — it would
 * count tags and truncate mid-tag — so the editor enforces `data-max-length` itself, on the visible
 * text: the same `textContent` measure the counter shows and the server applies. The server still
 * re-checks on save; IME composition cannot be cancelled mid-word, so that one path relies on it.
 */
function readMaxLength(textarea) {
  const maxLength = parseInt(textarea.dataset.maxLength, 10);

  return Number.isFinite(maxLength) && maxLength > 0 ? maxLength : null;
}

function selectedLength(editable) {
  const selection = window.getSelection();

  if (!selection?.rangeCount) return 0;

  const range = selection.getRangeAt(0);

  return editable.contains(range.commonAncestorContainer) ? range.toString().length : 0;
}

/** Characters that can still go in, counting any selection the insert will replace as free. */
function roomLeft(editable, maxLength) {
  return maxLength - (editable.textContent.length - selectedLength(editable));
}

/**
 * Cut text to fit, never leaving half an emoji: String.length counts UTF-16 code units, as the server
 * does, so a cut can land between a surrogate pair.
 */
function fitText(text, room) {
  if (room <= 0) return "";
  if (text.length <= room) return text;

  const cut = text.slice(0, room);
  const last = cut.charCodeAt(cut.length - 1);

  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

function incomingText(event) {
  if (typeof event.data === "string") return event.data;

  return event.dataTransfer?.getData("text/plain") ?? "";
}

function syncToTextarea(textarea, editable, flag) {
  textarea.value = editable.innerHTML;

  // The server reads this to decide whether to encode the value or treat it as markup.
  if (flag) flag.value = "true";

  // Anything listening for the counter is listening on the textarea.
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

function updateButtonStates(buttons) {
  buttons.forEach(({ button, command }) => {
    let active = false;

    try {
      active = document.queryCommandState(command);
    } catch {
      // queryCommandState throws when the selection is outside the editable. Not an error.
    }

    button.setAttribute("aria-pressed", String(active));
    button.classList.toggle("is-active", active);
  });
}

function buildToolbar(textarea, editable, flag) {
  const toolbar = document.createElement("div");
  toolbar.className = "rich-text-field__toolbar";
  toolbar.setAttribute("role", "toolbar");

  const buttons = COMMANDS.map((command) => {
    const button = document.createElement("button");

    // type="button" or it submits the form — the default for a button inside one is submit.
    button.type = "button";
    button.className = `rich-text-field__button rich-text-field__button--${command}`;
    button.dataset.testid = `profile-bio-${command}`;
    button.setAttribute("aria-pressed", "false");

    // Copy resolved server-side, so no user-facing string lives in this bundle.
    const label = textarea.dataset[command === "bold" ? "labelBold" : "labelItalic"] || command;
    button.setAttribute("aria-label", label);
    button.textContent = command === "bold" ? "B" : "I";

    // mousedown, not click: click fires after the editable has already lost the selection, so the
    // command would apply to nothing.
    button.addEventListener("mousedown", (event) => {
      event.preventDefault();
      editable.focus();
      document.execCommand(command, false, null);
      syncToTextarea(textarea, editable, flag);
      updateButtonStates(buttons);
    });

    toolbar.appendChild(button);

    return { button, command };
  });

  return { toolbar, buttons };
}

function upgrade(textarea) {
  const flag = textarea.dataset.richTextFlag
    ? document.getElementById(textarea.dataset.richTextFlag)
    : null;

  const wrapper = document.createElement("div");
  wrapper.className = "rich-text-field";

  const editable = document.createElement("div");
  editable.className = "rich-text-field__input my-profile__bio-textarea";
  editable.contentEditable = "true";
  editable.dataset.testid = "profile-bio-editor";
  editable.setAttribute("role", "textbox");
  editable.setAttribute("aria-multiline", "true");

  if (textarea.placeholder) editable.dataset.placeholder = textarea.placeholder;

  // Seeded from the STORED HTML, not from the textarea — the textarea holds the plain-text
  // fallback, and opening the editor on that would drop the member's existing formatting.
  editable.innerHTML = textarea.dataset.initialHtml || "";

  const { toolbar, buttons } = buildToolbar(textarea, editable, flag);

  wrapper.appendChild(toolbar);
  wrapper.appendChild(editable);
  textarea.parentNode.insertBefore(wrapper, textarea);

  // Hidden rather than removed: it is still the field that posts.
  textarea.hidden = true;

  // Paste as plain text. Otherwise a paste from Word arrives as a document's worth of markup that
  // the sanitiser then throws away — the member sees their formatting vanish on save and cannot
  // tell why.
  const maxLength = readMaxLength(textarea);

  // Trimmed here rather than in beforeinput: an execCommand insert does not fire beforeinput.
  editable.addEventListener("paste", (event) => {
    event.preventDefault();
    let text = (event.clipboardData || window.clipboardData).getData("text/plain");

    if (maxLength) text = fitText(text, roomLeft(editable, maxLength));

    if (text) document.execCommand("insertText", false, text);
  });

  // Typing, drops and autocorrect replacements. Line breaks and deletions add no visible text, so
  // only inserts that carry text are checked; an over-long one is cut to fit rather than refused.
  if (maxLength) {
    editable.addEventListener("beforeinput", (event) => {
      if (!event.inputType.startsWith("insert") || !event.cancelable) return;

      const text = incomingText(event);
      const room = roomLeft(editable, maxLength);

      if (!text || text.length <= room) return;

      event.preventDefault();

      const fitted = fitText(text, room);

      if (fitted) document.execCommand("insertText", false, fitted);
    });
  }

  editable.addEventListener("input", () => syncToTextarea(textarea, editable, flag));
  editable.addEventListener("keyup", () => updateButtonStates(buttons));
  editable.addEventListener("mouseup", () => updateButtonStates(buttons));
  editable.addEventListener("focus", () => updateButtonStates(buttons));

  // Sync once so the flag and the textarea agree with what is on screen before any editing.
  syncToTextarea(textarea, editable, flag);
}

function RichTextField() {
  const fields = document.querySelectorAll(".js-rich-text-field");

  if (!fields?.length) return;

  fields.forEach((textarea) => {
    // contentEditable is absent on very old browsers; leaving the textarea alone is the right
    // outcome there, not a broken editor.
    if (typeof document.execCommand !== "function") return;

    upgrade(textarea);
  });
}

function initRichTextField() {
  if (document.readyState !== "loading") {
    RichTextField();
  } else {
    document.addEventListener("DOMContentLoaded", RichTextField);
  }
}

export default initRichTextField;
